import { DOMEventDispatcher } from 'apps/webcomponents/src/utils/event.utils';
import { LitElement, html, PropertyValues } from 'lit';
import { state, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { GzDialogEvents } from 'apps/webcomponents/src/ui/gzDialog/component/GzDialog';
import { GzInputWebComponent } from '../../../../ui/gzInput/component/GzInputWebComponent';
import { InputWidthValues } from '@nirvana/shared/dtos/types/generic-dashboards/gz-console';
import { translateFn } from '../../../../utils/translator.helper';
import { FontSizes, FontWeights } from 'apps/webcomponents/src/types/common/types';
import { FormGroup } from '../../../../ui/gzForm/formGroup/form-group';
import { GzFormFieldWebComponent } from '../../../../ui/gzForm/formField';
import { RequiredValidator } from '../../../../ui/gzForm/validators/required.validator';
import { CustomizableValidator } from '../../../../ui/gzForm/validators/customizable.validator';
import { GzCheckboxWebComponent } from '../../../../ui/gzCheckbox';
import { MinLengthValidator } from '../../../../ui/gzForm/validators/min-length.validator';
import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from '../../../computersAndVirtualMachines/types';
import { RegexValidator } from '../../../../ui/gzForm/validators/regex.validator';
import { GzCollapsableContainerWebComponent } from '../../../../ui/gzCollapsableContainer';
import { MaxLengthValidator } from "../../../../ui/gzForm/validators/max-length.validator";

export class DownloadFormWebComponent extends LitElement {
  @state()
  private translations = {
    fileName: null,
    password: null,
    confirmPassword: null,
    legalInfo: null,
    checkbox: null,
    retrieve: null,
    cancel: null,
  };

  private buttonStrings = {
    showMore: translateFn('Show More'),
    showLess: translateFn('Show Less'),
  };

  @query('#filename-field')
  private filenameField: GzFormFieldWebComponent<GzInputWebComponent, string>;

  @query('#password-field')
  private passwordField: GzFormFieldWebComponent<GzInputWebComponent, string>;

  @query('#confirm-password-field')
  private confirmPasswordField: GzFormFieldWebComponent<GzInputWebComponent, string>;

  @query('#checkbox-form-field')
  private checkboxField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  @query('#size-helper-wrapper')
  private sizeHelperWrapper: HTMLElement;

  @query('#size-helper-text')
  private sizeHelperText: HTMLElement;

  @query('gz-collapsable-container')
  private disclaimer: GzCollapsableContainerWebComponent;

  @state()
  public isValid = false;

  @state()
  private formGroup: FormGroup = new FormGroup({});

  static get styles() {
    return styles;
  }

  protected firstUpdated(changedProperties: PropertyValues): void {
    super.update(changedProperties);
    this.setupTranslations();
    this.setFormGroup();
    this.setValidators();
    this.setupDisclaimer();
  }

  private setFormGroup(): void {
    this.formGroup.addFormChild(['filename'], this.filenameField);
    this.formGroup.addFormChild(['password'], this.passwordField);
    this.formGroup.addFormChild(['confirmPassword'], this.confirmPasswordField);
    this.formGroup.addFormChild(['checkbox'], this.checkboxField);
  }

  private setupDisclaimer(): void {
    this.disclaimer.fixedContentHeight = 60;
    this.disclaimer.buttonStrings = this.buttonStrings;
  }

  private setValidators(): void {
    this.filenameField.Validators = [
      RequiredValidator('This field is required'),
      RegexValidator(
        /^[^\\/:"*?<>|]*$/,
        translateFn('The file name cannot contain the following special characters: \\ / : * ? " < > |'),
      ),
    ];
    this.checkboxField.Validators = [
      CustomizableValidator(value => {
        if (value) {
          return {
            valid: true,
          };
        }
        return {
          valid: false,
          error: '',
        };
      }),
    ];

    const passwordValidators = [
      RequiredValidator('This field is required'),
    ];

    const confirmPasswordValidators = [...passwordValidators];
    confirmPasswordValidators.push(
      CustomizableValidator(value => {
        if (value !== this.passwordField.value) {
          return {
            valid: false,
            error: translateFn('Passwords do not match'),
          };
        }
        if (!this.passwordField.valid) {
          this.passwordField.validate();
        }
        return {
          valid: true,
        };
      }),
    );

    passwordValidators.push(
      MinLengthValidator(
        PASSWORD_MIN_LENGTH,
        translateFn('The password must have at least {passwordMinLength} characters.', {
          passwordMinLength: PASSWORD_MIN_LENGTH,
        }),
      ),
      MaxLengthValidator(
        PASSWORD_MAX_LENGTH,
        translateFn('Password must not be longer than {passwordMaxLength} characters in length.', {
          passwordMaxLength: PASSWORD_MAX_LENGTH,
        }),
      ),
      CustomizableValidator(value => {
        if (value !== this.confirmPasswordField.value) {
          return {
            valid: false,
            error: translateFn('Passwords do not match'),
          };
        }

        if (!this.confirmPasswordField.valid) {
          this.confirmPasswordField.validate();
        }
        return {
          valid: true,
        };
      }),
    );

    this.passwordField.Validators = passwordValidators;
    this.confirmPasswordField.Validators = confirmPasswordValidators;

    this.formGroup.valid$.subscribe({
      next: valid => {
        this.isValid = valid;
      },
    });
  }

  private setupTranslations(): void {
    this.translations = {
      fileName: translateFn('File name*'),
      password: translateFn('Password*'),
      confirmPassword: translateFn('Confirm password*'),
      legalInfo: translateFn('You understand and agree that the files are provided "as is" and that by downloading the files you will bear all the risks, responsibility, and liability from such download and from any further use of a potentially downloaded malicious file. Bitdefender does not accept any responsibility or liability for the content of the downloaded files. Bitdefender shall not be liable for any loss or damage of whatsoever nature (direct, indirect, consequential, or other) whether arising in contract, tort, or otherwise, which may arise as a result of your download or of your use of the downloaded file. Moreover, you are prohibited from using such files for other reasons whatsoever, especially with malicious intent, other than for further internal analysis of the detected and quarantined malware files. You understand that the files may contain personal data and that by your download of the files Bitdefender will no longer be a joint controller with you for the purposes of further processing by you on such data; you shall be an independent controller for such collection and data processing with all the rights and obligations that such role entails under the applicable data protection laws. '),
      checkbox: translateFn('I accept the terms of this agreement'),
      retrieve: translateFn('Retrieve'),
      cancel: translateFn('Cancel'),
    };

    this.sizeHelperText.innerText = translateFn('The password is not stored in GravityZone. Make sure you save it in a safe location.');
  }

  private save(): void {
    if (this.isValid) {
      DOMEventDispatcher.dispatchGlobalEvent(this, GzDialogEvents.CONFIRM, this.formGroup.value);
    }
  }

  private cancel(): void {
    DOMEventDispatcher.dispatchGlobalEvent(this, GzDialogEvents.CANCEL);
  }

  render() {
    return html`
      <gz-form-field id='filename-field' class='ml-5 mt-5' label='${this.translations.fileName}'>
        <gz-input
          data-filename
          gz-form-input
          placeholder='${translateFn('Enter file name')}'
          inputWidth='${InputWidthValues.LARGE}'
        ></gz-input>
        <gz-input-error></gz-input-error>
      </gz-form-field>
      <gz-form-field id='password-field' class='ml-5' label='${this.translations.password}'>
        <gz-input
          data-password
          gz-form-input
          type='password'
          placeholder='${translateFn('Enter password')}'
          inputWidth='${InputWidthValues.LARGE}'
        ></gz-input>
        <gz-input-error></gz-input-error>
      </gz-form-field>
      <gz-form-field id='confirm-password-field' class='ml-5' label='${this.translations.confirmPassword}'>
        <gz-input
          data-confirm-password
          gz-form-input
          type='password'
          placeholder='${translateFn('Re-enter password')}'
          inputWidth='${InputWidthValues.LARGE}'
        ></gz-input>
        <gz-input-error></gz-input-error>
        <div id='size-helper-wrapper'>
          <p id='size-helper-text'></p>
        </div>
      </gz-form-field>
      <gz-title fontSize='${FontSizes.PARAGRAPH_12}' fontWeight='${FontWeights.SEMIBOLD}' class='disclaimer-title'>DISCLAIMER</gz-title>
      <gz-collapsable-container class='disclaimer'>
        <div class='disclaimer-text'>${this.translations.legalInfo}</div>
      </gz-collapsable-container>
      <gz-form-field class='ml-5 mt-2 mr-4' id='checkbox-form-field'>
        <gz-checkbox
          gz-form-input
          id='checkbox-input'
          label='${this.translations.checkbox}'
        ></gz-checkbox>
        <gz-input-error></gz-input-error>
      </gz-form-field>

      <div class='footer__container'>
        <gz-button data-confirm id='confirm' ?disabled=${!this.isValid} @click=${this.save}>
          ${this.translations.retrieve}
        </gz-button>
        <gz-button data-cancel id='cancel' type='outline' color='secondary' @click=${this.cancel}>
          ${this.translations.cancel}
        </gz-button>
      </div>
    `;
  }
}
